import React from 'react';
import { Box, List, ListItem, Stack, Typography } from '@mui/material';
import { JunoUser } from '@juno/client-api/model';
import { useDirectoryData } from '../Contexts/DirectoryContext';
import DirectoryPagination, { DirectoryPaginationSkeleton } from './DirectoryPagination';
import DirectoryTile, { SkeletonDirectoryTile } from './DirectoryTile';
import { ExtendedUser } from './SavedUsers';

interface FullDirectoryProps {
  startChat: (user: JunoUser) => void;
  setSelectedUser: (user: JunoUser | null) => void;
  setNewMeetingOpen: (value: boolean) => void;
  messengerDisabled: boolean;
  setQuickViewUser: (userId: string | null) => void;
  setUserQuickViewAnchorEl: (element: HTMLElement | null) => void;
}

const FullDirectory: React.FC<FullDirectoryProps> = ({
  startChat,
  setSelectedUser,
  setNewMeetingOpen,
  messengerDisabled,
  setQuickViewUser,
  setUserQuickViewAnchorEl,
}) => {
  const {
    showSavedUsers,
    numPerPage,
    filteredUsersQuery,
    filteredUsersCountQuery,
    allUsersCountQuery,
    handleSaveUser,
    loadingData,
    settings: { user },
  } = useDirectoryData();
  const currentUser = user as ExtendedUser;

  const { data: filteredUsers } = filteredUsersQuery;
  const { data: filteredUsersCount } = filteredUsersCountQuery;
  const { data: allUsersCount } = allUsersCountQuery;

  const isSaved = (user: JunoUser) => currentUser?.saved_users?.some((id) => id === user.id);

  return (
    <>
      <Stack direction='row' spacing={2} alignItems={'center'}>
        <Typography variant='h6' mb={2}>
          Full Directory
        </Typography>
        <Typography variant='caption' mb={2}>
          {filteredUsersCount?.count} of {allUsersCount?.count} Directory Results
        </Typography>
      </Stack>
      <Box>
        <List>
          {filteredUsers?.map((user, idx) => (
            <ListItem sx={{ pl: 0, pr: 0, pb: 0, mb: 0.5 }} key={`user-${idx}`}>
              <DirectoryTile
                user={user as JunoUser}
                selectUser={(user, e) => {
                  setQuickViewUser(user.id);
                  setUserQuickViewAnchorEl(e.target as HTMLElement);
                }}
                startMeeting={(user) => {
                  setSelectedUser(user);
                  setNewMeetingOpen(true);
                }}
                startChat={(user) => {
                  startChat(user);
                }}
                saveUser={(user) => handleSaveUser(user)}
                isSaved={isSaved(user)}
                messengerDisabled={messengerDisabled}
                showSaveButton={showSavedUsers}
              />
            </ListItem>
          ))}
        </List>
        {loadingData && (
          <>
            {[...Array(numPerPage)].map((_, idx) => (
              <SkeletonDirectoryTile key={idx} />
            ))}
            <DirectoryPaginationSkeleton />
          </>
        )}
        {filteredUsers && filteredUsers.length > 0 && <DirectoryPagination />}
      </Box>
    </>
  );
};

export default FullDirectory;
